import { styled } from '@mui/material';
import { getPortalZoomScale } from 'components/ui/utilities/zoom';
import { BRAND, NEUTRAL } from 'theme';

const GUIDED_TOUR_COMPACT_VIEWPORT_WIDTH_PX = 640;
const GUIDED_TOUR_PORTAL_ZOOM_SCALE_VAR = '--guided-tour-portal-zoom-scale';

export const getGuidedTourScaledPx = (value: number) =>
  `calc(${value}px * var(${GUIDED_TOUR_PORTAL_ZOOM_SCALE_VAR}, 1))`;

export const GuidedTourOverlay = styled('div', {
  shouldForwardProp: prop => prop !== 'hasSpotlight',
})<{ hasSpotlight?: boolean }>(({ hasSpotlight, theme }) => ({
  backgroundColor: hasSpotlight ? 'transparent' : 'rgba(1, 18, 32, 0.68)',
  inset: 0,
  pointerEvents: 'none',
  position: 'fixed',
  // Keep the dim layer below MUI menus/popovers so opened dropdowns stay readable.
  zIndex: theme.zIndex.modal - 1,
}));

export const GuidedTourInteractionBlocker = styled('div')(({ theme }) => ({
  backgroundColor: 'transparent',
  cursor: 'default',
  inset: 0,
  pointerEvents: 'auto',
  position: 'fixed',
  touchAction: 'none',
  // Above app menus/popovers, below the visual spotlight and tour card.
  zIndex: theme.zIndex.modal + 20,
}));

export const GuidedTourSpotlight = styled('div', {
  shouldForwardProp: prop => prop !== 'hasBackdrop' && prop !== 'isTrackingTarget',
})<{ hasBackdrop?: boolean; isTrackingTarget?: boolean }>(
  ({ hasBackdrop = true, isTrackingTarget = false, theme }) => ({
    backgroundColor: 'rgba(3, 125, 232, 0.08)',
    border: `2px solid ${BRAND[500]}`,
    borderRadius: theme.spacing(1.5),
    boxShadow: hasBackdrop
      ? '0 0 0 9999px rgba(1, 18, 32, 0.7), 0 16px 40px rgba(15, 23, 42, 0.24)'
      : '0 8px 22px rgba(3, 125, 232, 0.24)',
    pointerEvents: 'none',
    position: 'fixed',
    transition: isTrackingTarget
      ? 'none'
      : theme.transitions.create(['height', 'left', 'top', 'width'], {
        duration: theme.transitions.duration.shorter,
        easing: theme.transitions.easing.easeOut,
      }),
    zIndex: theme.zIndex.modal + 21,
  })
);

export const GuidedTourActionPointer = styled('span', {
  shouldForwardProp: prop => prop !== 'isClicking',
})<{ isClicking?: boolean }>(({ isClicking, theme }) => ({
  alignItems: 'center',
  background: isClicking ? BRAND[500] : NEUTRAL.white,
  border: `2px solid ${isClicking ? BRAND[700] : BRAND[500]}`,
  borderRadius: 999,
  boxShadow: isClicking ? '0 12px 36px rgba(3, 125, 232, 0.52)' : '0 10px 30px rgba(3, 125, 232, 0.35)',
  color: isClicking ? NEUTRAL.white : BRAND[600],
  display: 'inline-flex',
  height: isClicking ? 14 : 12,
  justifyContent: 'center',
  pointerEvents: 'none',
  position: 'fixed',
  animation: isClicking ? 'guided-tour-pointer-press 620ms cubic-bezier(0.2, 0.8, 0.2, 1)' : undefined,
  transition: theme.transitions.create(
    ['background-color', 'border-color', 'box-shadow', 'height', 'left', 'top', 'width'],
    {
      duration: theme.transitions.duration.shorter,
      easing: theme.transitions.easing.easeInOut,
    }
  ),
  transform: 'translate(-50%, -50%)',
  width: isClicking ? 14 : 12,
  zIndex: theme.zIndex.modal + 23,
  '&::before': {
    animation: isClicking
      ? 'guided-tour-pointer-click 560ms cubic-bezier(0.2, 0.8, 0.2, 1)'
      : 'guided-tour-pointer-pulse 1200ms ease-in-out infinite',
    background: isClicking ? NEUTRAL.white : BRAND[500],
    borderRadius: 999,
    content: '""',
    height: isClicking ? 4 : 3,
    left: '50%',
    position: 'absolute',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    transformOrigin: 'center',
    width: isClicking ? 4 : 3,
  },
  '&::after': {
    animation: isClicking ? 'guided-tour-pointer-ring-click 620ms ease-out' : undefined,
    border: `2px solid ${isClicking ? BRAND[400] : BRAND[300]}`,
    borderRadius: 999,
    content: '""',
    inset: isClicking ? -7 : -4,
    opacity: isClicking ? 0.9 : 0.62,
    position: 'absolute',
  },
  '@keyframes guided-tour-pointer-pulse': {
    '0%, 100%': {
      transform: 'translate(-50%, -50%) scale(1)',
    },
    '45%': {
      transform: 'translate(-50%, -50%) scale(1.5)',
    },
  },
  '@keyframes guided-tour-pointer-press': {
    '0%': {
      transform: 'translate(-50%, -50%) scale(1)',
    },
    '34%': {
      transform: 'translate(-50%, -50%) scale(0.78)',
    },
    '100%': {
      transform: 'translate(-50%, -50%) scale(1.18)',
    },
  },
  '@keyframes guided-tour-pointer-click': {
    '0%': {
      transform: 'translate(-50%, -50%) scale(1)',
    },
    '35%': {
      transform: 'translate(-50%, -50%) scale(0.35)',
    },
    '100%': {
      transform: 'translate(-50%, -50%) scale(1.75)',
    },
  },
  '@keyframes guided-tour-pointer-ring-click': {
    '0%': {
      opacity: 0.95,
      transform: 'scale(0.8)',
    },
    '100%': {
      opacity: 0,
      transform: 'scale(3.8)',
    },
  },
}));

export const GuidedTourCard = styled('section', {
  shouldForwardProp: prop => prop !== 'zoomLevel',
})<{ zoomLevel: number }>(({ theme, zoomLevel }) => ({
  [GUIDED_TOUR_PORTAL_ZOOM_SCALE_VAR]: getPortalZoomScale(zoomLevel),
  background: NEUTRAL.white,
  border: `1px solid ${NEUTRAL[200]}`,
  borderRadius: getGuidedTourScaledPx(12),
  boxSizing: 'border-box',
  boxShadow: '0 18px 56px rgba(15, 23, 42, 0.3)',
  color: theme.palette.text.primary,
  display: 'grid',
  gap: getGuidedTourScaledPx(10),
  maxWidth: getGuidedTourScaledPx(300),
  padding: getGuidedTourScaledPx(14),
  position: 'fixed',
  maxHeight: `calc(100vh - ${getGuidedTourScaledPx(24)})`,
  overflowY: 'auto',
  transition: theme.transitions.create(['bottom', 'left', 'opacity', 'right', 'top', 'transform', 'width'], {
    duration: theme.transitions.duration.shorter,
    easing: theme.transitions.easing.easeOut,
  }),
  width: `min(${getGuidedTourScaledPx(300)}, calc(100vw - ${getGuidedTourScaledPx(32)}))`,
  zIndex: theme.zIndex.modal + 22,
}));

export const GuidedTourEyebrow = styled('span')({
  color: BRAND[600],
  fontSize: getGuidedTourScaledPx(9),
  fontWeight: 700,
  letterSpacing: '0.1em',
  lineHeight: 1,
  textTransform: 'uppercase',
});

export const GuidedTourTitle = styled('h2')({
  color: NEUTRAL[900],
  fontSize: getGuidedTourScaledPx(14),
  fontWeight: 700,
  lineHeight: 1.25,
  margin: 0,
});

export const GuidedTourBody = styled('div')({
  color: NEUTRAL[600],
  fontSize: getGuidedTourScaledPx(12),
  lineHeight: 1.45,
});

export const GuidedTourActions = styled('div')(({ theme }) => ({
  alignItems: 'flex-end',
  display: 'flex',
  gap: `calc(${theme.spacing(1)} * var(${GUIDED_TOUR_PORTAL_ZOOM_SCALE_VAR}, 1))`,
  justifyContent: 'space-between',
  paddingTop: `calc(${theme.spacing(0.25)} * var(${GUIDED_TOUR_PORTAL_ZOOM_SCALE_VAR}, 1))`,
  [`@media (max-width: ${GUIDED_TOUR_COMPACT_VIEWPORT_WIDTH_PX}px)`]: {
    alignItems: 'stretch',
    flexWrap: 'wrap',
    gap: `calc(${theme.spacing(0.75)} * var(${GUIDED_TOUR_PORTAL_ZOOM_SCALE_VAR}, 1))`,
  },
}));

export const GuidedTourActionGroup = styled('span')(({ theme }) => ({
  alignItems: 'center',
  display: 'inline-flex',
  gap: `calc(${theme.spacing(0.75)} * var(${GUIDED_TOUR_PORTAL_ZOOM_SCALE_VAR}, 1))`,
  [`@media (max-width: ${GUIDED_TOUR_COMPACT_VIEWPORT_WIDTH_PX}px)`]: {
    flex: '1 1 auto',
    justifyContent: 'flex-end',
  },
}));

export const GuidedTourProgress = styled('span')({
  color: NEUTRAL[500],
  fontSize: getGuidedTourScaledPx(10),
  fontWeight: 600,
  position: 'absolute',
  right: getGuidedTourScaledPx(12),
  top: getGuidedTourScaledPx(10),
});
